import type { PersistedClient } from '@tanstack/query-persist-client-core'

import type { CacheGeneration } from './cache-scope'
import type { SnapshotPersister } from './writer-snapshots'
import { cacheKeyOwner, cacheUserPrefixes } from './cache-scope'

type SqliteDB = Awaited<
  ReturnType<typeof import('@tauri-apps/plugin-sql').default.load>
>

let dbPromise: Promise<SqliteDB> | null = null

async function getDb(): Promise<SqliteDB> {
  if (!dbPromise) {
    dbPromise = (async () => {
      const mod = await import('@tauri-apps/plugin-sql')
      const db = await mod.default.load('sqlite:cache.db')
      await db.execute(`CREATE TABLE IF NOT EXISTS query_cache (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL,
        updated_at INTEGER NOT NULL
      )`)
      await db.execute(
        'CREATE TABLE IF NOT EXISTS deleted_cache_owners (user_id TEXT PRIMARY KEY)',
      )
      await db.execute(
        'CREATE TABLE IF NOT EXISTS cache_owner_generations (user_id TEXT PRIMARY KEY, generation TEXT NOT NULL)',
      )
      await db.execute(
        'CREATE TABLE IF NOT EXISTS cache_forget_operations (user_id TEXT NOT NULL, operation_id TEXT NOT NULL, completed INTEGER NOT NULL DEFAULT 0, PRIMARY KEY (user_id, operation_id))',
      )
      return db
    })().catch(error => {
      dbPromise = null
      throw error
    })
  }
  return dbPromise
}

export async function removeSQLiteCaches(
  prefix: string,
  expected?: CacheGeneration,
): Promise<void> {
  const db = await getDb()
  await db.execute(
    'DELETE FROM query_cache WHERE substr(key, 1, length($1)) = $1 AND ($2 IS NULL OR (SELECT generation FROM cache_owner_generations WHERE user_id = $2) IS $3)',
    [prefix, expected?.ownerId ?? null, expected?.generation ?? null],
  )
}

export async function deleteSQLiteUserCaches(userId: string): Promise<void> {
  const db = await getDb()
  await db.execute(
    'INSERT OR IGNORE INTO deleted_cache_owners (user_id) VALUES ($1)',
    [userId],
  )
  for (const prefix of cacheUserPrefixes(userId))
    await removeSQLiteCaches(prefix)
}

/** Every phase is conditional on the same unfinished operation. This remains
 * safe across pooled SQL connections and crashes between statements. */
export async function forgetSQLiteUserCaches(
  userId: string,
  operationId: string,
): Promise<void> {
  if (!userId || !operationId || operationId.length > 128)
    throw new Error('Cache removal requires an owner and operation')
  const db = await getDb()
  await db.execute(
    'INSERT OR IGNORE INTO cache_forget_operations (user_id, operation_id) VALUES ($1, $2)',
    [userId, operationId],
  )
  await db.execute(
    `INSERT INTO cache_owner_generations (user_id, generation)
    SELECT $1, $2 WHERE EXISTS (SELECT 1 FROM cache_forget_operations WHERE user_id = $1 AND operation_id = $2 AND completed = 0)
    ON CONFLICT(user_id) DO UPDATE SET generation = excluded.generation`,
    [userId, operationId],
  )
  for (const prefix of cacheUserPrefixes(userId))
    await db.execute(
      'DELETE FROM query_cache WHERE substr(key, 1, length($1)) = $1 AND EXISTS (SELECT 1 FROM cache_forget_operations WHERE user_id = $2 AND operation_id = $3 AND completed = 0)',
      [prefix, userId, operationId],
    )
  await db.execute(
    'UPDATE cache_forget_operations SET completed = 1 WHERE user_id = $1 AND operation_id = $2',
    [userId, operationId],
  )
}

/** Rows written for this scope by other writers. `schema-` rows are not writers. */
const OTHER_SCOPE_WRITERS = `substr(key, 1, length($1)) = $1 AND substr(key, length($1) + 1, 7) != 'schema-' AND key != $2`
const OWNER_CURRENT = `NOT EXISTS (SELECT 1 FROM deleted_cache_owners WHERE user_id = $3)
  AND NOT EXISTS (SELECT 1 FROM cache_forget_operations WHERE user_id = $3 AND completed = 0)
  AND (SELECT generation FROM cache_owner_generations WHERE user_id = $3) IS $4`

export async function createSQLitePersister(
  cacheKey: string,
  scopePrefix = cacheKey,
): Promise<SnapshotPersister> {
  const db = await getDb()
  const owner = cacheKeyOwner(cacheKey)
  const generation = owner
    ? ((
        await db.select<{ generation: string }[]>(
          'SELECT generation FROM cache_owner_generations WHERE user_id = $1',
          [owner],
        )
      )[0]?.generation ?? null)
    : null
  /** The exact row this writer restored, including its paused writes. */
  let adopted: { key: string; updatedAt: number } | null = null
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        const value = JSON.stringify(client)
        // Assign order in SQLite so writes within one millisecond or after a
        // clock adjustment still restore in commit order across windows.
        const written = await db.select<{ key: string; updated_at: number }[]>(
          `INSERT OR REPLACE INTO query_cache (key, value, updated_at)
           SELECT $1, $2, max($3, coalesce((SELECT max(updated_at) + 1 FROM query_cache
             WHERE substr(key, 1, length($6)) = $6), $3))
           WHERE NOT EXISTS (SELECT 1 FROM deleted_cache_owners WHERE user_id = $4)
           AND NOT EXISTS (SELECT 1 FROM cache_forget_operations WHERE user_id = $4 AND completed = 0)
           AND (SELECT generation FROM cache_owner_generations WHERE user_id = $4) IS $5
           RETURNING key, updated_at`,
          [cacheKey, value, Date.now(), owner, generation, scopePrefix],
        )
        if (written.length === 0) return false
        if (scopePrefix === cacheKey) return true
        const updatedAt = written[0]!.updated_at
        // Prune superseded writers once this row is durable: no paused offline
        // writes (counted natively, off the UI thread), or the row restored
        // here. A newer row from a concurrent window is left alone.
        await db.execute(
          `DELETE FROM query_cache WHERE ${OTHER_SCOPE_WRITERS} AND ${OWNER_CURRENT}
           AND EXISTS (SELECT 1 FROM query_cache WHERE key = $2 AND updated_at = $5)
           AND updated_at <= $5
           AND (
             CASE WHEN json_valid(value)
               THEN coalesce(json_array_length(value, '$.clientState.mutations'), 0) = 0
               ELSE 1 END
             OR (key = $6 AND updated_at = $7)
           )`,
          [
            scopePrefix,
            cacheKey,
            owner,
            generation,
            updatedAt,
            adopted?.key ?? null,
            adopted?.updatedAt ?? null,
          ],
        )
        adopted = null
        return true
      } catch {
        console.warn('[QueryCache:SQLite] Failed to persist client')
        return false
      }
    },
    restoreClient: async () => {
      try {
        // Pick the newest row by key and timestamp first so only one cached
        // value is read and parsed. Two windows can write in the same
        // millisecond; rowid breaks that tie by write order (INSERT OR
        // REPLACE gives every write a rowid above all remaining rows).
        const rows = await db.select<
          { key: string; value: string; updated_at: number }[]
        >(
          `SELECT key, value, updated_at FROM query_cache WHERE key = (
             SELECT key FROM query_cache WHERE (key = $2 OR (${OTHER_SCOPE_WRITERS})) AND ${OWNER_CURRENT}
             ORDER BY CASE WHEN key = $2 THEN 0 ELSE 1 END, updated_at DESC, rowid DESC LIMIT 1
           )`,
          [scopePrefix, cacheKey, owner, generation],
        )
        if (rows.length === 0) return undefined
        const [row] = rows
        if (row.key !== cacheKey)
          adopted = { key: row.key, updatedAt: row.updated_at }
        return JSON.parse(row.value) as PersistedClient
      } catch {
        console.warn('[QueryCache:SQLite] Failed to restore client')
        return undefined
      }
    },
    removeClient: async () => {
      try {
        await removeSQLiteCaches(
          scopePrefix,
          owner ? { ownerId: owner, generation } : undefined,
        )
      } catch {
        console.warn('[QueryCache:SQLite] Failed to remove client')
      }
    },
  }
}
